import React from 'react';
import type { GetStaticPropsResult } from 'next';
import dynamic from 'next/dynamic';
import { gql } from '@soundxyz/gql-string';
import { MobileContentVisibility } from '@components/MobileContentVisibility';
import { Seo } from '@components/Seo';
import type { SharedProps } from '@components/layout';
import type {
  ExecutionResultWithData,
  HeroHighlightedReleasesQuery,
  ViralSoundsLandingQuery,
} from '@graphql/reactQuery';
import {
  fetchGQL,
  HeroHighlightedReleasesDocument,
  LearnMoreFooterDocument,
  ViralSoundsLandingDocument,
} from '@graphql/reactQuery';

/**
 * We dynamically import these components so they are not chunking up the main bundle.
 * This helps it put in separate chunks which can be loaded in parallel by the browser.
 * This is a good way to improve performance.
 *
 * Important is we need to set SSR to TRUE so that these components are rendered on the server
 * allowing us to ship the HTML structure that is generated on the server.
 */
const Hero = dynamic(() => import('@components/Landing/sections/Hero').then(mod => mod.Hero), {
  ssr: true,
});

const ViralSounds = dynamic(
  () => import('@components/Landing/sections/ViralSounds').then(mod => mod.ViralSounds),
  { ssr: true },
);

const Upcoming = dynamic(
  () => import('@components/Landing/sections/Upcoming').then(mod => mod.UpcomingDropsSection),
  { ssr: true },
);

const FreshlyMintedSelector = dynamic(
  () =>
    import('@components/Landing/sections/FreshlyMintedSelector').then(
      mod => mod.FreshlyMintedSelector,
    ),
  { ssr: true },
);

const TopCollectors = dynamic(
  () => import('@components/Landing/sections/TopCollectors').then(mod => mod.TopCollectors),
  { ssr: true },
);

const ViralPlaylists = dynamic(
  () => import('@components/Landing/sections/ViralPlaylists').then(mod => mod.ViralPlaylists),
  { ssr: true },
);

const RecentDrops = dynamic(
  () => import('@components/Landing/sections/RecentDrops').then(mod => mod.RecentDropsSection),
  { ssr: true },
);

/*#__PURE__*/ gql(/* GraphQL */ `
  query HeroHighlightedReleases {
    highlightedReleases {
      id
      track {
        id
      }
      ...LandingHeroBackground
      ...LandingHeroSlide
    }
  }
`);

export async function getStaticProps(): Promise<GetStaticPropsResult<PageProps>> {
  return Promise.all([
    // This query is used to get the featured releases for the landing page
    // We need to get this data on the server so we can prefill the apollo cache
    fetchGQL(HeroHighlightedReleasesDocument, {}),
    fetchGQL(ViralSoundsLandingDocument, {
      variables: {
        pagination: { first: 10, after: null },
      },
    }),
    fetchGQL(LearnMoreFooterDocument, {}),
  ]).then(([highlightedReleases, viralSounds, learnMore]) => {
    return {
      props: {
        highlightedReleases,
        viralSounds,
        learnMore,
      },
      // rebuild every 2 minutes
      revalidate: 120,
    };
  });
}

interface PageProps extends SharedProps {
  highlightedReleases: ExecutionResultWithData<HeroHighlightedReleasesQuery>;
  viralSounds: ExecutionResultWithData<ViralSoundsLandingQuery>;
}

export default function Home({ highlightedReleases, viralSounds }: PageProps) {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Seo />
      <Hero highlightedReleases={highlightedReleases.data.highlightedReleases} />

      <div className="mx-auto mb-8 mt-6 flex w-full flex-col gap-y-8 md:mt-8 lt:mb-12 lt:mt-8 lt:gap-y-12 2xl:max-w-content">
        <MobileContentVisibility visibility="auto" sizeY={500}>
          <ViralSounds initialData={viralSounds} />
        </MobileContentVisibility>

        <MobileContentVisibility visibility="auto" sizeY={420}>
          <FreshlyMintedSelector />
        </MobileContentVisibility>

        <MobileContentVisibility visibility="auto" sizeY={429}>
          <TopCollectors />
        </MobileContentVisibility>

        <MobileContentVisibility visibility="auto" sizeY={416}>
          <RecentDrops />
        </MobileContentVisibility>

        <MobileContentVisibility visibility="auto" sizeY={351}>
          <ViralPlaylists />
        </MobileContentVisibility>

        <MobileContentVisibility visibility="auto" sizeY={428}>
          <Upcoming heading="Upcoming Drops: Artists You Follow" />
        </MobileContentVisibility>
      </div>
    </div>
  );
}
